import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import Typography from "@mui/joy/Typography";
import Link from "@mui/joy/Link";
import { theme } from "../../styles/theme";
import { useQuery } from "@tanstack/react-query";
import { paymentApi } from "../../api/paymentApi";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import { trackXAdsEvent } from "analytics/xAdsPixel";
import { isAdTrackingSuppressed } from "analytics/adTracking";
import useMixpanelMKT from "../../hooks/useMixpanelMKT";
import { userSimpleAtom } from "../../recoil/userSimpleAtom";
import { useRecoilValue } from "recoil";
import { getAdChannel, getFunnelPaths } from "routes/adFunnel";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import useGtagConversion from "../../hooks/useGtagConversion";
import { getPromotionCode } from "../../components/task/payment/planTypes";

export default function TossPaymentsPromotionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get("orderId");
  const paymentKey = searchParams.get("paymentKey");
  const payAmount = Number(searchParams.get("amount"));

  useAutoLoginPromotion();
  const user = useRecoilValue(userSimpleAtom);

  const trackGtagEvent = useGtagConversion();

  const trackPromotionPurchaseSuccess = useMixpanelMKT({
    eventName: "결제 완료",
    version: "v4",
  });

  const { isLoading, isSuccess, isError } = useQuery({
    queryKey: ["tossPromoConfirm", orderId, paymentKey, payAmount],
    enabled: Boolean(
      orderId && paymentKey && payAmount && user?.trackId && user?.userId
    ),
    queryFn: async () => {
      return await paymentApi.confirmTossPaymentsPromotion({
        orderId: orderId as string,
        paymentKey: paymentKey as string,
        payAmount: payAmount as number,
        version: "v4",
        // 프로모션 유저 정합성 교차검증용 영문 코드 (user/v2/simple 이 promotion 을 내려줄 때만 전송됨)
        promotionCode: getPromotionCode(user?.promotion),
        // 결제 지점(메타/구글 퍼널) — 서버 Slack 결제 로그 표기용
        adChannel: getAdChannel(),
      });
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (responseData) => {
      if (
        responseData?.result?.isConfirmed !== true &&
        responseData?.result?.failureMessage
      ) {
        alert(responseData?.result?.failureMessage);
        return;
      }

      // try {
      //   // 결제 성공 후 재진입 시 결제 페이지의 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송을 1회 차단
      //   localStorage.setItem("suppress_payment_page_view", "1");
      // } catch (e) {}

      // 이후 이벤트 전송
      // 광고 전환(Meta CAPI·Google Ads·X Ads)은 내부자·제휴 프로모션 유저 제외 — 메인 앱과 같은 기준. Mixpanel 은 항상 전송
      if (!isAdTrackingSuppressed(user)) {
        trackCapiStandardEvent(
          "Purchase",
          {
            value: payAmount,
            currency: "KRW",
            order_id: orderId ?? undefined,
            externalId: user?.userId,
            email: user?.email,
          },
          {
            eventId: orderId ?? undefined,
          }
        );

        trackGtagEvent("Purchase", "v4", {
          transactionId: orderId ?? undefined,
          value: payAmount,
          currency: "KRW",
          email: user?.email,
          sha256EmailAddress: user?.sha256EmailAddress,
          sha256PhoneNumber: user?.sha256PhoneNumber,
          // 웹퍼널 결제는 구독 중(isSubscribing)·프로모션 기사용(hasAlreadyUsed) 유저가 차단되므로 항상 첫 구매
          newCustomer: true,
          user,
        });

        trackXAdsEvent("Purchase", {
          value: payAmount,
          currency: "KRW",
          email: user?.email,
          conversionId: orderId ?? undefined,
        });
      }

      trackPromotionPurchaseSuccess();
    },
    onError: () => {
      // 이 confirm 이 실패로 보인다고 결제가 안 된 것이 아니다. 신규 구독은 승인 뒤 채팅방 개설까지 동기로
      // 도는데 axios 타임아웃이 15초라, 승인은 끝났는데 응답만 못 받는 경우가 있다. 여기서 "다시 시도해
      // 주세요" 라고 안내하면 유저가 새 주문으로 한 번 더 결제한다 — 실제 이중 결제가 이 문구로 났다.
      alert(
        "결제 결과를 확인하지 못했어요. 이미 결제가 됐을 수 있으니 다시 결제하지 마시고, support@orwellhealth.org 로 문의해 주세요."
      );
    },
  });

  useEffect(() => {
    if (isSuccess) {
      navigate(getFunnelPaths().completed, { replace: true });
    }
  }, [isSuccess, navigate]);

  if (!orderId || !paymentKey || !payAmount) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>{"만료된 페이지"}</Typography>
          </Stack>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>
              {"결제 결과를 확인하지 못했어요."}
            </Typography>
            <Typography variant={"H3"}>
              {"이미 결제가 됐을 수 있으니 다시 결제하지 마시고,"}
            </Typography>
            <Typography variant={"H3"}>
              <Link
                href="mailto:support@orwellhealth.org"
                sx={{
                  textDecoration: "underline",
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                support@orwellhealth.org
              </Link>
              {"로 문의해 주세요."}
            </Typography>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  return null;
}
